import { List } from 'immutable';

export const appIsVisible = (getState) => {
    const { app } = getState();
    return app.get('visible', false) && app.get('focus', false);
};

export const updatePushTypeSetting = (
    pushTypes,
    pushTypeSetting,
    settingsType,
) => {
    return pushTypes.set(
        pushTypes.findIndex(
            (pushType) => pushType.get('name') === settingsType,
        ),
        pushTypeSetting,
    );
};

export const updateProfileNotificationType = (
    pushTypeSetting,
    profileKey,
    device,
    value,
) => {
    const defaultSetting = getDefaultNotificationSetting(
        pushTypeSetting.get('notificationSettings'),
    );
    return pushTypeSetting.update('notificationSettings', (prev) => {
        const index = prev.findIndex(
            (setting) => setting.getIn(['profile', 'key']) === profileKey,
        );

        if (index === -1) {
            const profileDTO = {
                key: profileKey,
            };

            const settingsDTO = defaultSetting
                .set('profile', profileDTO)
                .set(device, value);

            return prev.push(settingsDTO);
        } else if (defaultSetting.get(device) === value) {
            // If the device value is set to the default value,
            // check if we want to remove the profileSetting.
            const updatedSetting = prev.get(index).set(device, value);
            const sameAsDefault = settingSameAsDefault(
                updatedSetting,
                defaultSetting,
            );
            // If the profileSetting behaves as default, remove profileSetting.
            return sameAsDefault
                ? prev.delete(index)
                : prev.set(index, updatedSetting);
        } else {
            return prev.update(index, (setting) => setting.set(device, value));
        }
    });
};

/**
 * Checks if the notification settings are the same for all profiles
 * for all listed devices
 *
 * @param pushTypes (list of settings and profiles)
 * @returns true if one or more profile settings varies
 */
export const hasVaryingNotifications = (pushTypes) => {
    if (pushTypes) {
        return List(['desktop', 'mobile', 'sms', 'email'])
            .map((device) => variesNotifications(pushTypes, device))
            .some((device) => device === true);
    } else {
        return false;
    }
};

/**
 * Validate if the notification settings are the same for all profiles.
 *
 * @param pushTypes (list of settings and profiles)
 * @param device one of 'desktop', 'mobile', 'sms', 'email'
 * @returns true if one or more profile settings varies
 */
export const variesNotifications = (pushTypes, device) => {
    return pushTypes
        .map((type) => presentVaryingNotificationProfile(type, device))
        .some((varyingType) => varyingType === true);
};

/**
 * Validate if all profiles match the settings for the chosen device.
 *
 * @param pushType (list of settings and profiles)
 * @param device one of 'desktop', 'mobile', 'sms', 'email'
 * @return true if one or more settings match the criteria
 */
export const presentVaryingNotificationProfile = (pushType, device) => {
    const notificationSettings = pushType.get('notificationSettings');
    const defaultSetting = getDefaultNotificationSetting(notificationSettings);

    return notificationSettings.some(
        (setting) =>
            setting.has(device) &&
            setting.get(device) !== defaultSetting.get(device),
    );
};

/**
 * Extract the default NotificationSettingDTO from the notificationSettings.
 * If the NotificationSettingDTO is missing a profile, it is considered the default.
 *
 * @param notificationSettings from a PushTypeDTO
 *
 * @return the default NotificationSettingDTO or null if none is found.
 */

export const getDefaultNotificationSetting = (notificationSettings) => {
    return notificationSettings.find(
        (setting) => !setting.has('profile'),
        null,
        null,
    );
};

/**
 * Check if the supplied setting has the same configuration as the supplied defaultSetting.
 *
 * @param setting NotificationSettingDTO
 * @param defaultSetting NotificationSettingDTO
 *
 * @return true if the supplied settings have the same configuration, otherwise false.
 */
export const settingSameAsDefault = (setting, defaultSetting) => {
    return !defaultSetting
        .map(
            (defValue, forDevice) =>
                defValue === setting.get(forDevice, defValue),
        )
        .includes(false);
};

/**
 * Check if the supplied PushTypeDTO contains any unneccessary profile NotificationSettingDTO.
 * If they have the same configuration as the default NotificationSettingDTO, it is considered unneccessary.
 * If so, remove the NotificationSettingDTO.
 *
 * @param pushType PushTypeDTO
 *
 * @return PushTypeDTO with unneccessary NotificationSettingDTO's removed.
 */
export const removeUnusedProfileSettings = (pushType) => {
    const notificationSettings = pushType.get('notificationSettings');
    const defaultSetting = getDefaultNotificationSetting(notificationSettings);

    const updatedNotificationSetttings = notificationSettings.filter(
        (setting) =>
            !settingSameAsDefault(setting, defaultSetting) ||
            !setting.has('profile'),
    );
    return pushType.set('notificationSettings', updatedNotificationSetttings);
};
